import { BankReference } from '@dill/dill-shared/dist/src';
import React from 'react';
import { DillButton } from '../../../general/Dill/DillButton';
import {
  ArrowCircleUpRight,
  CheckCircle,
  DownloadSimple,
  Eye,
  Timer,
  Trash,
  Warning,
} from 'phosphor-react';
import { COLORS } from '../../../../utils/colors';
import { Edit, Mail } from 'react-feather';
import { TradeReferenceHistory } from './TradeReferenceHistory';
import CryptoJS from 'crypto-js';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  getSupplierCreditApplication,
  sendBankReference,
  updateBankReferenceResponse,
} from '../../../../redux/services/creditApplicationsService';
import { openDillToast } from '../../../../utils/helpers';

interface Props {
  bankReferences: BankReference[];
  isRequired?: boolean;
  onRemove?: (index: number) => void;
  onEdit?: (index: number) => void;
  onAddBankReferenceResponse?: (bankReference: BankReference) => void;
  onPreviewFile?: (url: string, fileType: string) => void;
  onDownloadFile?: (url: string, fileName: string) => void;
  isPreview?: boolean;
  creditApplicationId?: string;
  applicationHistoryId?: string | null;
  className?: string;
}

interface FieldRowProps {
  label: string;
  value: string;
  isRequired?: boolean;
  half?: boolean;
  dataPrivate?: boolean;
}

const FieldRow = ({ label, value, isRequired, half, dataPrivate }: FieldRowProps) => (
  <div className={half ? 'w-1/2' : 'w-full'}>
    <p className="text-[11px] font-semibold text-gray-500 mb-0.5">
      {label}
      {isRequired && <span className="text-red-500 ml-0.5">*</span>}
    </p>
    <p className="text-sm text-gray-900" {...(dataPrivate ? { 'data-private': true } : {})}>
      {value}
    </p>
  </div>
);

/** Masks a bank account number down to its last 4 digits, e.g. "****6789". */
const maskAccountNumber = (accountNumber: string) =>
  accountNumber.length <= 4 ? `****${accountNumber}` : `****${accountNumber.slice(-4)}`;

const ShowBankReferenceDetails: React.FC<Props> = ({
  bankReferences,
  isRequired,
  onRemove,
  onEdit,
  onAddBankReferenceResponse,
  onPreviewFile,
  onDownloadFile,
  isPreview,
  creditApplicationId: creditApplicationIdProp,
  applicationHistoryId,
  className,
}) => {
  const dispatch = useAppDispatch();
  const { creditApplication } = useAppSelector((state) => state.creditApplications);
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const creditApplicationId = creditApplicationIdProp ?? creditApplication?.id ?? '';

  const handleRemoveFile = async (bankRef: BankReference) => {
    try {
      const secretKey = process.env.REACT_APP_CRYPTO_KEY;
      if (!secretKey) throw new Error('Missing encryption key');
      const encryptedAccountNumber = CryptoJS.AES.encrypt(
        JSON.stringify(bankRef?.bankDetails?.accountNumber ?? ''),
        secretKey
      ).toString();
      const updatedBankReference = {
        ...bankRef,
        bankDetails: { ...bankRef.bankDetails, accountNumber: encryptedAccountNumber },
      };
      const result = await dispatch(
        updateBankReferenceResponse({
          supplierId: currentSupplier?.id ?? '',
          bankReference: updatedBankReference,
          creditApplicationId,
          ...(applicationHistoryId ? { applicationHistoryId } : {}),
        })
      );
      if (result.type.includes('fulfilled')) {
        openDillToast({
          type: 'SUCCESS',
          message: 'Successfully updated bank reference response.',
        });
        dispatch(
          getSupplierCreditApplication({
            supplierId: currentSupplier?.id || '',
            creditApplicationId,
            hashedType: 'encrypted',
            fetchType: 'detailed',
            skipLoading: 'true',
          })
        );
      } else {
        openDillToast({ type: 'ERROR', message: 'Failed to update bank reference response.' });
      }
    } catch (error: any) {
      openDillToast({
        type: 'ERROR',
        message: error?.message || 'Failed to update bank reference response.',
      });
    }
  };

  const handleSendEmail = async (bankRef: BankReference) => {
    try {
      const secretKey = process.env.REACT_APP_CRYPTO_KEY;
      if (!secretKey) throw new Error('Missing encryption key');
      const encryptedAccountNumber = CryptoJS.AES.encrypt(
        JSON.stringify(bankRef?.bankDetails?.accountNumber ?? ''),
        secretKey
      ).toString();
      const updatedBankReference = {
        ...bankRef,
        bankDetails: { ...bankRef.bankDetails, accountNumber: encryptedAccountNumber },
      };
      const latestAction = bankRef.approvalStatusHistory?.length
        ? bankRef.approvalStatusHistory[bankRef.approvalStatusHistory.length - 1].action
        : null;
      const emailType: 'initial' | 'followUp' =
        latestAction?.toLowerCase() === 'requested' ? 'followUp' : 'initial';
      const result = await dispatch(
        sendBankReference({
          supplierId: currentSupplier?.id ?? '',
          bankReference: updatedBankReference,
          creditApplicationId,
          emailType,
          ...(applicationHistoryId ? { applicationHistoryId } : {}),
        })
      );
      if (result.type.includes('fulfilled')) {
        openDillToast({ type: 'SUCCESS', message: 'Successfully sent bank reference email.' });
        dispatch(
          getSupplierCreditApplication({
            supplierId: currentSupplier?.id || '',
            creditApplicationId,
            hashedType: 'encrypted',
            fetchType: 'detailed',
            skipLoading: 'true',
          })
        );
      } else {
        openDillToast({ type: 'ERROR', message: 'Failed to send bank reference email.' });
      }
    } catch (error: any) {
      openDillToast({
        type: 'ERROR',
        message: error?.message || 'Failed to send bank reference email.',
      });
    }
  };

  return (
    <div className={`flex flex-col gap-3 w-full${className ? ` ${className}` : ''}`}>
      {bankReferences.map((bankReference, index) => {
        const latestAction = bankReference.approvalStatusHistory?.length
          ? bankReference.approvalStatusHistory[bankReference.approvalStatusHistory.length - 1]
              .action
          : null;
        const statusLabel =
          latestAction?.toLowerCase() === 'requested' || latestAction?.toLowerCase() === 'followup'
            ? 'Requested'
            : latestAction?.toLowerCase() === 'submitted'
            ? 'Submitted'
            : 'Not Requested';
        const statusClass =
          statusLabel === 'Requested'
            ? 'WARNING_100-BG WARNING_500-CLR'
            : statusLabel === 'Submitted'
            ? 'SUCCESS_100-BG SUCCESS_500-CLR'
            : 'GREY_100-BG GREY_500-CLR';
        const StatusIcon =
          statusLabel === 'Submitted' ? CheckCircle : statusLabel === 'Requested' ? Timer : Warning;

        const d = bankReference.bankDetails;

        return (
          <div
            key={index}
            className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            {/* Card header */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
              <p className="text-sm font-semibold text-gray-800">
                {d.bankName && d.bankName.trim() !== ''
                  ? d.bankName
                  : `Bank Reference ${bankReferences.length > 1 ? index + 1 : ''}`}
              </p>
              <div className="flex items-center gap-2">
                {isPreview &&
                  bankReference.approvalStatusHistory &&
                  bankReference.approvalStatusHistory.length > 0 && (
                    <TradeReferenceHistory
                      buttonTitle="View History"
                      buttonType="TERTIARY"
                      buttonStyles={{ height: '28px' }}
                      referenceHistory={bankReference.approvalStatusHistory.map((h) => ({
                        action: h.action,
                        date: h.date instanceof Date ? h.date.toISOString() : String(h.date),
                        actionBy: h.actionBy,
                        note: h.notes ?? '',
                      }))}
                    />
                  )}
                {isPreview && (
                  <div
                    className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-semibold ${statusClass}`}>
                    <StatusIcon size={13} weight="bold" />
                    <span>{statusLabel}</span>
                  </div>
                )}
                {isPreview &&
                  d.email &&
                  latestAction?.toLowerCase() !== 'submitted' &&
                  (latestAction?.toLowerCase() === 'requested' ||
                  latestAction?.toLowerCase() === 'followup' ? (
                    <button
                      className="flex items-center gap-1 text-xs PRIMARY_500-CLR font-semibold cursor-pointer"
                      onClick={() => handleSendEmail(bankReference)}>
                      <span>Follow Up</span>
                      <ArrowCircleUpRight size={14} />
                    </button>
                  ) : (
                    <DillButton
                      label="Send Email"
                      onClick={() => handleSendEmail(bankReference)}
                      variant="secondary"
                      icon={<Mail className={COLORS.PRIMARY_500} size={14} />}
                      height="30px"
                      width="130px"
                    />
                  ))}
                {onEdit && (
                  <button
                    type="button"
                    aria-label="Edit bank reference"
                    onClick={() => onEdit(index)}
                    className="inline-flex items-center justify-center text-gray-400 hover:text-blue-600">
                    <Edit size={14} />
                  </button>
                )}
                {onRemove && (
                  <button
                    onClick={() => onRemove(index)}
                    className="text-red-400 hover:text-red-600 font-bold text-sm leading-none">
                    ✕
                  </button>
                )}
              </div>
            </div>

            {/* Fields grid */}
            <div className="px-4 py-3 flex flex-col gap-2">
              {/* Row 1: Account Number (bank name already in header) */}
              {d.accountNumber?.trim() && (
                <FieldRow
                  label="Account Number"
                  isRequired={isRequired}
                  value={maskAccountNumber(d.accountNumber)}
                  dataPrivate
                />
              )}

              {/* Row 2: Email + Phone */}
              {(d.email?.trim() || d.phoneNumber?.trim()) && (
                <div className="flex gap-4">
                  {d.email?.trim() && (
                    <FieldRow label="Email" value={d.email} isRequired={isRequired} half />
                  )}
                  {d.phoneNumber?.trim() && (
                    <FieldRow
                      label="Phone Number"
                      value={d.phoneNumber}
                      isRequired={isRequired}
                      half
                    />
                  )}
                </div>
              )}

              {/* Address */}
              {d.address?.trim() && (
                <FieldRow label="Address" value={d.address} isRequired={isRequired} />
              )}

              {/* City / State / Zip */}
              {(d.city?.trim() || d.state?.trim() || d.zip?.trim()) && (
                <div className="flex gap-4">
                  {d.city?.trim() && (
                    <FieldRow label="City" value={d.city} isRequired={isRequired} half />
                  )}
                  {d.state?.trim() && (
                    <FieldRow label="State" value={d.state} isRequired={isRequired} half />
                  )}
                  {d.zip?.trim() && (
                    <FieldRow label="Zip" value={d.zip} isRequired={isRequired} half />
                  )}
                </div>
              )}

              {/* Contact Person */}
              {d.contactPerson?.trim() && (
                <FieldRow label="Contact Person" value={d.contactPerson} isRequired={isRequired} />
              )}
            </div>

            {/* Bank Reference Response */}
            {bankReference.referenceResponse && (
              <div className="mx-4 mb-3 border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                    Bank Reference Response
                  </p>
                  {onAddBankReferenceResponse && (
                    <DillButton
                      variant="ghost-blue"
                      icon={<Edit size={13} />}
                      height={24}
                      width={24}
                      onClick={() => onAddBankReferenceResponse(bankReference)}
                    />
                  )}
                </div>
                <div className="p-3 flex flex-col gap-1">
                  {bankReference.referenceResponse.textResponse && (
                    <p className="text-xs text-gray-700">
                      {bankReference.referenceResponse.textResponse}
                    </p>
                  )}
                  {bankReference.referenceResponse.files &&
                    bankReference.referenceResponse.files.length > 0 && (
                      <div className="flex flex-col gap-1">
                        {bankReference.referenceResponse.files.map((file, idx) => (
                          <div key={`bank-ref-file-${idx}`} className="flex gap-1 items-center">
                            <DillButton
                              variant="icon-default"
                              width="25px"
                              height="25px"
                              onClick={(e: any) => {
                                e.preventDefault();
                                onDownloadFile?.(file.url ?? '', file?.name ?? '');
                              }}
                              icon={<DownloadSimple color={COLORS.GREY_500} size={14} />}
                            />
                            {['pdf', 'jpg', 'jpeg', 'png'].includes(file?.fileType ?? '') && (
                              <DillButton
                                variant="icon-default"
                                width="25px"
                                height="25px"
                                icon={<Eye color={COLORS.GREY_500} size={14} />}
                                onClick={() =>
                                  onPreviewFile &&
                                  onPreviewFile(file?.url ?? '', file?.fileType ?? '')
                                }
                              />
                            )}
                            <span className="text-xs text-gray-700 flex-1 truncate">
                              {file?.name ?? ''}
                            </span>
                            <DillButton
                              variant="icon-default"
                              width="25px"
                              height="25px"
                              icon={<Trash color={COLORS.ERROR_500} size={14} />}
                              onClick={() => {
                                const filteredFiles = (
                                  bankReference?.referenceResponse?.files ?? []
                                ).filter((_, i) => i !== idx);
                                handleRemoveFile({
                                  ...bankReference,
                                  referenceResponse: {
                                    ...bankReference.referenceResponse,
                                    files: filteredFiles,
                                  },
                                });
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              </div>
            )}

            {/* Add response button */}
            {onAddBankReferenceResponse && !bankReference.referenceResponse && isPreview && (
              <div className="px-4 pb-3">
                <DillButton
                  height="30px"
                  variant="ghost-blue"
                  label="Add Bank Reference Response"
                  onClick={() => onAddBankReferenceResponse(bankReference)}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ShowBankReferenceDetails;
